import { transformCmsStorelocatorResponse } from '../storelocator/transformers.js';
import { transformCatalogProductResponse, transformProductOptions } from '../transformers/product/index.js';
import { transformBundleOptions } from '../transformers/bundleOptions/index.js';
import { transformConfigurableOptions } from '../transformers/configurableOptions/index.js';
import { transformCustomOptions } from '../transformers/customOptions/index.js';
import { transformGroupedOptions } from '../transformers/groupedOptions/index.js';
import { transformGiftCardOptions } from '../transformers/giftcardOptions/index.js';
import { transformCustomerAddress } from '../transformers/customer/index.js';
import { transformOrderTotals } from '../transformers/order/index.js';

const transformOrderItem = ({ order_item_id , sku , name , qty , price , metadata , product_data , totals , product_options  })=>{
    const productData = transformCatalogProductResponse(product_data);
    const transformedMetadata = {
        configurableOptions: transformConfigurableOptions(metadata?.configurable_options, product_data?.variants),
        customOptions: transformCustomOptions(metadata?.custom_options),
        bundleOptions: transformBundleOptions(metadata?.bundle_options),
        groupedOptions: transformGroupedOptions(metadata?.grouped_options),
        giftcardOptions: transformGiftCardOptions(metadata?.giftcard_item_option, productData)
    };
    return {
        orderItemId: order_item_id,
        sku,
        name,
        qty,
        price,
        metadata: transformedMetadata,
        productData: transformCatalogProductResponse(product_data),
        totals,
        productOptions: transformProductOptions(product_options, productData, transformedMetadata)
    };
};
const transformCreditMemoInvoiceShipments = ({ id , created_at , updated_at , customer_data , items , totals , creditmemo_number =null , invoice_number =null , shipment_number =null , tracking_data =null  })=>({
        id,
        createdAt: created_at,
        updatedAt: updated_at,
        email: customer_data.email,
        items: items.map((item)=>transformOrderItem(item)),
        totals: totals ? transformOrderTotals(totals) : null,
        number: creditmemo_number || invoice_number || shipment_number || null,
        trackingData: tracking_data?.map(({ title , track_number , track_url  } = {})=>({
                title,
                trackNumber: track_number,
                trackUrl: track_url || null
            })) || null
    });
const transformItem = ({ id , sku , parent_sku , name , qty , price , product_type , metadata , product_data , totals , product_options , qty_invoiced , qty_shipped , ...rest })=>{
    const productData = transformCatalogProductResponse(product_data);
    const { configurable_options , custom_options , bundle_options , grouped_options , giftcard_item_option , ...restMetadata } = metadata || {};
    const transformedMetadata = {
        configurableOptions: transformConfigurableOptions(configurable_options, product_data?.variants),
        customOptions: transformCustomOptions(custom_options),
        bundleOptions: transformBundleOptions(bundle_options),
        groupedOptions: transformGroupedOptions(grouped_options),
        giftcardOptions: transformGiftCardOptions(giftcard_item_option, productData),
        __rest: restMetadata
    };
    return {
        id,
        sku,
        parentSku: parent_sku,
        name,
        qty,
        price,
        productType: product_type,
        metadata: transformedMetadata,
        productData,
        totals,
        productOptions: transformProductOptions(product_options, productData, transformedMetadata),
        qtyInvoiced: qty_invoiced,
        qtyShipped: qty_shipped,
        __rest: rest
    };
};
const transformOrderDetails = ({ id , order_number =null , created_at , status , protect_code , payment_data , shipping_data , customer_data , items , billing_address , totals , invoices =null , shipments =null , creditmemos =null , returnable_items =null , iban_required_for_return , is_in_store =false , physical_store =null , ...rest })=>({
        id,
        number: order_number,
        createdAt: created_at,
        status,
        protectCode: protect_code,
        paymentMethod: payment_data?.payment_method,
        shippingMethod: shipping_data?.shipping_method,
        shippingMethodLabel: shipping_data?.shipping_method_name,
        shippingAddress: shipping_data?.shipping_address ? transformCustomerAddress(shipping_data.shipping_address) : null,
        billingAddress: billing_address ? transformCustomerAddress(billing_address) : null,
        email: customer_data?.email,
        totals: totals ? transformOrderTotals(totals) : null,
        items: items?.map((item)=>transformItem(item)),
        invoices: invoices ? invoices.map((item)=>transformCreditMemoInvoiceShipments(item)) : null,
        returnableItems: returnable_items ? returnable_items.map(({ item_id , returnable_qty  })=>({
                id: item_id,
                qty: returnable_qty
            })) : null,
        shipments: shipments ? shipments.map((item)=>transformCreditMemoInvoiceShipments(item)) : null,
        creditmemos: creditmemos ? creditmemos.map((item)=>transformCreditMemoInvoiceShipments(item)) : null,
        isVirtual: !Object.keys(shipping_data?.shipping_address ?? {}).length > 0,
        ibanRequiredForReturn: iban_required_for_return ?? false,
        isInStore: is_in_store,
        storeDetails: physical_store ? transformCmsStorelocatorResponse(physical_store) : null,
        __rest: rest
    });
const transformCrmCustomerMeOrdersResponse = (orders)=>orders.map((order)=>transformOrderDetails(order));
const transformCrmCustomerMeOrderResponse = (order)=>transformOrderDetails(order);
const transformCrmCustomerPaymentVaultMeResponse = (data)=>data.map(({ public_hash , payment_method_code , type , created_at , expires_at , details , is_active , logo  })=>({
            publicHash: public_hash,
            paymentMethodCode: payment_method_code,
            type,
            createdAt: created_at,
            expiresAt: expires_at,
            details: type === 'card' ? {
                cardType: details[0],
                lastDigit: details[1],
                expiryDate: details[2]
            } : {},
            isActive: is_active,
            imageUrl: logo
        }));
const transformCrmCustomerMeOrdersV2Response = ({ count , items  })=>({
        count,
        items: items.map((order)=>transformOrderDetails(order))
    });

export { transformCrmCustomerMeOrderResponse, transformCrmCustomerMeOrdersResponse, transformCrmCustomerMeOrdersV2Response, transformCrmCustomerPaymentVaultMeResponse };
